import { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '../api/apiClient';
import { useSocket } from './useSocket';

/**
 * Load persisted provisioning logs and subscribe for live updates.
 * Uses socket + polling so a stalled HTTP approve stream cannot freeze the UI.
 */
export function useProvisioningLogs(requestId, userEmail, { enabled = true } = {}) {
    const [logs, setLogs] = useState([]);
    const [status, setStatus] = useState(null);
    const [requestStatus, setRequestStatus] = useState(null);
    const [isRunning, setIsRunning] = useState(false);
    const [startedAt, setStartedAt] = useState(null);
    const [cloudName, setCloudName] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const logsLengthRef = useRef(0);
    const isRunningRef = useRef(false);
    const { socket, connected } = useSocket(enabled && requestId ? userEmail : null);

    const applySnapshot = useCallback((data) => {
        if (!data) return;
        const nextLogs = Array.isArray(data.logs) ? data.logs : [];
        setLogs(nextLogs);
        logsLengthRef.current = nextLogs.length;
        setStatus(data.status ?? null);
        setRequestStatus(data.requestStatus ?? null);
        const running = !!data.isRunning;
        setIsRunning(running);
        isRunningRef.current = running;
        setStartedAt(data.startedAt || null);
        setCloudName(data.cloudName || null);
    }, []);

    const fetchLogs = useCallback(async (fromIndex = 0) => {
        if (!requestId) return null;
        if (fromIndex === 0) setLoading(true);
        setError(null);
        try {
            const res = await apiClient.get(`/api/requests/${requestId}/provisioning-logs`, {
                params: { fromIndex }
            });
            const data = res.data;
            if (fromIndex > 0 && Array.isArray(data.logs) && data.logs.length > 0) {
                setLogs((prev) => {
                    const merged = [...prev];
                    data.logs.forEach((line) => {
                        if (!merged.length || merged[merged.length - 1] !== line) {
                            merged.push(line);
                        }
                    });
                    logsLengthRef.current = merged.length;
                    return merged;
                });
                setStatus(data.status ?? null);
                setRequestStatus(data.requestStatus ?? null);
                const running = !!data.isRunning;
                setIsRunning(running);
                isRunningRef.current = running;
                if (data.startedAt) setStartedAt(data.startedAt);
                if (data.cloudName) setCloudName(data.cloudName);
            } else if (fromIndex === 0) {
                applySnapshot(data);
            } else if (data && typeof data.isRunning === 'boolean') {
                setIsRunning(data.isRunning);
                isRunningRef.current = data.isRunning;
                setStatus(data.status ?? null);
                setRequestStatus(data.requestStatus ?? null);
            }
            return data;
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to load logs');
            return null;
        } finally {
            if (fromIndex === 0) setLoading(false);
        }
    }, [requestId, applySnapshot]);

    // Initial load
    useEffect(() => {
        if (!enabled || !requestId) {
            setLogs([]);
            setStatus(null);
            setIsRunning(false);
            isRunningRef.current = false;
            setStartedAt(null);
            logsLengthRef.current = 0;
            return;
        }
        fetchLogs(0);
    }, [enabled, requestId, fetchLogs]);

    // Poll while running so a dead socket/stream cannot stall the UI
    useEffect(() => {
        if (!enabled || !requestId) return undefined;
        const interval = setInterval(() => {
            fetchLogs(logsLengthRef.current);
        }, 2500);
        return () => clearInterval(interval);
    }, [enabled, requestId, fetchLogs]);

    // Live socket updates
    useEffect(() => {
        if (!enabled || !requestId || !socket || !connected) return undefined;

        const room = `logs:${requestId}`;
        socket.emit('join-room', room);

        const onLog = (payload) => {
            if (!payload || payload.requestId !== requestId) return;
            if (payload.type === 'heartbeat') return;
            const message = payload.message;
            if (!message) return;
            setLogs((prev) => {
                if (prev.length && prev[prev.length - 1] === message) return prev;
                // Deduplicate if poll already added this line
                if (prev.includes(message) && prev[prev.length - 1] !== message) {
                    // still append if it's a repeat of an older line (unlikely); prefer skip only exact trailing dup
                }
                const next = [...prev, message];
                logsLengthRef.current = next.length;
                return next;
            });
            setIsRunning(true);
            isRunningRef.current = true;
        };

        const onStatus = (payload) => {
            if (!payload || payload.requestId !== requestId) return;
            setStatus(payload.status || null);
            if (payload.requestStatus) setRequestStatus(payload.requestStatus);
            if (payload.status && payload.status !== 'running' && payload.status !== 'cancel_requested') {
                setIsRunning(false);
                isRunningRef.current = false;
                // Final refresh so we don't miss trailing lines
                fetchLogs(0);
            }
        };

        socket.on('provision:log', onLog);
        socket.on('provision:status', onStatus);

        return () => {
            socket.off('provision:log', onLog);
            socket.off('provision:status', onStatus);
        };
    }, [enabled, requestId, socket, connected, fetchLogs]);

    const cancelProvisioning = useCallback(async () => {
        if (!requestId) return;
        await apiClient.post(`/api/requests/${requestId}/cancel-provisioning`);
        setLogs((prev) => [...prev, `Cancel requested`]);
    }, [requestId]);

    return {
        logs,
        setLogs,
        status,
        requestStatus,
        isRunning,
        startedAt,
        cloudName,
        loading,
        error,
        connected,
        fetchLogs,
        cancelProvisioning
    };
}

export const PROVISIONING_IN_PROGRESS_STATUSES = [
    'VM_CREATION_IN_PROGRESS',
    'VM_CREATED',
    'REMOTE_SETUP_IN_PROGRESS',
    'REMOTE_SETUP_COMPLETED'
];

export function isProvisioningInProgress(status) {
    if (!status) return false;
    return PROVISIONING_IN_PROGRESS_STATUSES.includes(status);
}

export default useProvisioningLogs;
